import { FlexCol } from '@/components/common/Flex';
import { LinkText, Paragraph, Text } from '@/components/common/Typography';
import IsAmountKnownBottomContent from '@/components/goals/IsAmountKnownBottomContent';
import client from '@/services/graphql/client';
import { toDollars } from '@/util/currency';
import { ListItem, UnorderedList } from '@chakra-ui/react';
import { sortedMetros } from '@common/data/zillow';
import { FV } from '@common/financeMath';
import { HomeDownPaymentGoal } from '@common/goals';
import { HomePriceInfoDocument } from '@common/graphql/generated';
import { getGoalBaseConfig } from '@common/react/goals';
import { HomePriceInfo, HomeSize } from '@common/types';
import { dt, isPastDate } from '@common/util/dateTime';
import { capitalize } from '@common/util/strings';
import { isNumber, isString } from '@common/util/types';
import isEqual from 'lodash.isequal';
import GoalStatementLinkText from '../GoalStatementLinkText';
import {
    getFinalAssumptionsWithRisk,
    getIOrWe,
    getRecommendedRiskScoreUsingTargetDate,
    getTargetDateFromYearMonth,
    getYearsOldStr,
} from '../helpers';
import { GoalConfig, GoalData } from './types';

const homeSizeLabelByValue: Record<HomeSize, string> = {
    CONDO: 'Condo',
    '1_BED': '1 bedroom',
    '2_BED': '2 bedroom',
    '3_BED': '3 bedroom',
    '4_BED': '4 bedroom',
    '5_BED': '5 bedroom',
} as const;

export const HOME_DOWN_PAYMENT: GoalConfig<HomeDownPaymentGoal> = {
    inputSteps: [
        {
            id: 'isCostKnown',
            title: 'Do you know how much your house may cost?',
            fieldConfigs: [
                {
                    fieldName: 'isCostKnown',
                    fieldType: 'radioCards',
                    fieldOptions: [
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                    ],
                    fieldValueDefault: 'false',
                    bottomContent: ({ goalData }) => (
                        <IsAmountKnownBottomContent
                            blogHref="https://www.withplenty.com/blog/buying-your-first-home-what-you-need-to-know"
                            blogLinkText="See Plenty’s guide on buying a home"
                            isAmountKnown={goalData.isCostKnown}
                        />
                    ),
                },
            ],
            onNext: ({ goalData, goForward }) => {
                if (goalData.isCostKnown === true) {
                    goForward('yearMonth');
                } else {
                    goForward();
                }
            },
        },
        {
            id: 'metro',
            title: 'Where do you want your dream home?',
            fieldConfigs: [
                {
                    fieldName: 'metro',
                    fieldType: 'select',
                    fieldOptions: [
                        ...sortedMetros.map((metro) => ({
                            value: metro,
                            label: metro,
                        })),
                    ],
                    bottomContent: (
                        <Paragraph fontSize="sm" color="textMuted" mt={8}>
                            {`Our data comes from Zillow; if your city isn’t in this list, please select a nearby city with similar house prices.`}
                        </Paragraph>
                    ),
                    placeholder: 'City, State',
                },
            ],
        },
        {
            id: 'homeSize',
            title: `What's your ideal house size?`,
            fieldConfigs: [
                {
                    fieldName: 'homeSize',
                    fieldType: 'select',
                    fieldOptions: Object.entries(homeSizeLabelByValue).map(
                        ([value, label]) => ({ value, label })
                    ),
                    fieldValueDefault: '3_BED',
                    bottomContent: ({ goalData, goBack }) =>
                        goalData.metro ? (
                            <Paragraph fontSize="sm" color="textMuted" mt={4}>
                                {`Using average house prices from ${goalData.metro}. `}
                                <LinkText
                                    fontSize="sm"
                                    onClick={() => goBack()}
                                >
                                    Change
                                </LinkText>
                            </Paragraph>
                        ) : undefined,
                },
            ],
        },
        {
            id: 'yearMonth',
            title: 'Roughly, when do you want to buy your new house?',
            subtitle:
                'This is just a starting point to create your goal, you’ll finalize your investment amount later.',
            fieldConfigs: [
                {
                    fieldName: 'yearMonth',
                    fieldType: 'yearMonth',
                    bottomContent: ({ goalData, userInfo }) => (
                        <Paragraph fontSize="sm" color="textMuted" mt={4} h={5}>
                            {goalData.targetDate
                                ? getYearsOldStr({
                                      date: goalData.targetDate,
                                      userInfo,
                                  })
                                : undefined}
                        </Paragraph>
                    ),
                },
            ],
            onBack: ({ goalData, goBack }) => {
                if (goalData.isCostKnown === true) {
                    goBack('isCostKnown');
                } else {
                    goBack();
                }
            },
            isNextButtonDisabled: (goalData) =>
                !goalData.targetDate || isPastDate(goalData.targetDate),
        },
        {
            id: 'homePrice',
            title: 'Choose a target house price that you feel comfortable with.',
            fieldConfigs: [
                {
                    fieldName: 'homePrice',
                    fieldType: 'currency',
                    fieldValueDefault: 0,
                    min: 0,
                    slider: ({ goalData }) => {
                        return goalData.projectedHomePriceInfo
                            ? {
                                  min: 0,
                                  max:
                                      goalData.projectedHomePriceInfo.amount *
                                      2,
                                  step: 10_000_00, // $10,000
                              }
                            : {
                                  min: 0,
                                  max: 2_000_000_00, // $2M
                                  step: 10_000_00, // $10,000
                              };
                    },
                    bottomContent: ({ goalData, showSlideup }) =>
                        !goalData.isCostKnown &&
                        goalData.homeSize &&
                        goalData.targetDate &&
                        goalData.projectedHomePriceInfo ? (
                            <>
                                <Paragraph
                                    fontSize="sm"
                                    color="textMuted"
                                    mt={4}
                                >
                                    {`The projected price of a ${
                                        homeSizeLabelByValue[goalData.homeSize]
                                    } in ${
                                        goalData.projectedHomePriceInfo
                                            .locationName
                                    } in ${dt(goalData.targetDate).format(
                                        'MMMM, YYYY'
                                    )} is ${toDollars(
                                        goalData.projectedHomePriceInfo.amount
                                    )}. Courtesy of Zillow.`}
                                </Paragraph>
                                <LinkText
                                    fontSize="sm"
                                    fontWeight="medium"
                                    mt={3}
                                    onClick={() => {
                                        showSlideup('targetAmountAssumptions');
                                    }}
                                >
                                    See assumptions
                                </LinkText>
                            </>
                        ) : undefined,
                },
            ],
        },
        {
            id: 'downPaymentFraction',
            title: ({ goalData }) =>
                `How much do you want to pay for the down payment on a ${
                    goalData.homePrice ? toDollars(goalData.homePrice) : ''
                } house?`,

            fieldConfigs: [
                {
                    fieldName: 'downPaymentFraction',
                    fieldType: 'percent',
                    fieldValueDefault: 0.1,
                    slider: {
                        min: 0,
                        max: 1, // 100%
                        step: 0.01, // 1%
                    },
                    bottomContent: ({ goalData, showSlideup }) => {
                        return (
                            <FlexCol mt={4}>
                                {goalData.targetAmount ? (
                                    <Text fontSize="lg" color="textMuted">
                                        {toDollars(goalData.targetAmount)}
                                    </Text>
                                ) : null}
                                <Paragraph
                                    mt={4}
                                    fontSize="sm"
                                    color="textMuted"
                                >
                                    According to REALTORS, the average down
                                    payment for first time home buyers is 7%;
                                    overall is 13%.
                                </Paragraph>
                                <LinkText
                                    fontSize="sm"
                                    fontWeight="medium"
                                    mt={1}
                                    onClick={() => {
                                        showSlideup(
                                            'downPaymentFractionAssumptions'
                                        );
                                    }}
                                >
                                    See assumptions
                                </LinkText>
                            </FlexCol>
                        );
                    },
                },
                {
                    fieldName: 'includeClosingCostsAndFees',
                    fieldType: 'checkbox',
                    fieldLabel: 'Include closing costs, fees, etc.',
                    fieldValueDefault: true,
                },
            ],
        },
    ],
    assumptions: [
        {
            id: 'targetAmountAssumptions',
            title: 'Assumptions',
            content: () => (
                <>
                    <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                        Your estimated house price is calculated using:
                    </Paragraph>
                    <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                        <ListItem>
                            Current average house prices in your target
                            location, courtesy of Zillow
                        </ListItem>
                        <ListItem>
                            Inflation for rising home prices based on the
                            20-year national average using Federal Housing
                            Finance Agency data (4.59% from 1992 to 2022)
                        </ListItem>
                        <ListItem>Timing of when you’d like to buy</ListItem>
                    </UnorderedList>
                </>
            ),
        },
        {
            id: 'downPaymentFractionAssumptions',
            title: 'Assumptions',
            content: () => (
                <>
                    <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                        For a mortgage, the down payment is the initial amount
                        paid to secure the loan:
                    </Paragraph>
                    <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                        <ListItem>
                            The national average for down payments is 13%
                        </ListItem>
                        <ListItem>
                            For first time home buyers, the percentage is 7%
                        </ListItem>
                        <ListItem>
                            There may be an additional cost of mortgage
                            insurance if your down payment is less than 10% .
                        </ListItem>
                    </UnorderedList>
                    <Paragraph fontSize="sm" lineHeight="tall" mt={4} mb={1}>
                        Closing costs are the additional expenses required to
                        secure a mortgage. They can include:
                    </Paragraph>
                    <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                        <ListItem>Pre-payment of property taxes</ListItem>
                        <ListItem>Pre-payment of property insurance</ListItem>
                        <ListItem>Legal expenses</ListItem>
                        <ListItem>Credit check</ListItem>
                        <ListItem>Appraisal fees</ListItem>
                    </UnorderedList>
                </>
            ),
        },
    ],
    getGoalStatementContent: ({ goalData, onGoalStatementClick }) => {
        if (
            !isNumber(goalData.targetAmount) ||
            !isNumber(goalData.homePrice) ||
            !isNumber(goalData.downPaymentFraction) ||
            !isString(goalData.targetDate)
        ) {
            console.warn(
                'Failed to construct goal statement due to incomplete goalData.'
            );
            console.warn(goalData);
            return null;
        }

        const iOrWe = getIOrWe(goalData);
        const color = HOME_DOWN_PAYMENT.color;

        return (
            <>
                {`${capitalize(iOrWe)} want to have `}
                <GoalStatementLinkText
                    text={toDollars(goalData.targetAmount)}
                    onClick={() => onGoalStatementClick('downPaymentFraction')}
                    color={color}
                />
                {' for '}
                <GoalStatementLinkText
                    text={`${
                        Math.floor(goalData.downPaymentFraction * 10000) / 100
                    }%`}
                    onClick={() => onGoalStatementClick('downPaymentFraction')}
                    color={color}
                />
                {' down on a new '}
                <GoalStatementLinkText
                    text={toDollars(goalData.homePrice)}
                    onClick={() => onGoalStatementClick('homePrice')}
                    color={color}
                />
                {' home by '}
                <GoalStatementLinkText
                    text={dt(goalData.targetDate).format('MMMM, YYYY')}
                    onClick={() => onGoalStatementClick('yearMonth')}
                    color={color}
                />
                {'.'}
            </>
        );
    },
    getAssumptionsContent: ({ goalData, userInfo, currentRateInfo }) =>
        getFinalAssumptionsWithRisk({
            goalData,
            recommendedRiskScore: HOME_DOWN_PAYMENT.getRecommendedRiskScore({
                goalData,
                userInfo,
            }),
            currentRateInfo,
        }),
    calculateDerivedGoalData: async ({
        oldGoalData,
        newGoalData,
    }): Promise<GoalData<HomeDownPaymentGoal>> => {
        const targetDate =
            oldGoalData.yearMonth !== newGoalData.yearMonth &&
            newGoalData.yearMonth?.length === 6
                ? getTargetDateFromYearMonth(newGoalData.yearMonth)
                : oldGoalData.targetDate;

        const projectedHomePriceInfo =
            oldGoalData.targetDate !== targetDate ||
            oldGoalData.homeSize !== newGoalData.homeSize ||
            oldGoalData.metro !== newGoalData.metro
                ? await getProjectedHomePriceInfo({
                      targetDate,
                      homeSize: newGoalData.homeSize,
                      metro: newGoalData.metro,
                  })
                : oldGoalData.projectedHomePriceInfo;

        const isProjectedHomePriceChanged = !isEqual(
            oldGoalData.projectedHomePriceInfo,
            projectedHomePriceInfo
        );

        const targetAmount = getDownPaymentAmount({
            homePrice: isProjectedHomePriceChanged
                ? projectedHomePriceInfo?.amount
                : newGoalData.homePrice,
            downPaymentFraction: newGoalData.downPaymentFraction,
            includeClosingCostsAndFees:
                newGoalData.includeClosingCostsAndFees ?? true,
        });

        return {
            ...newGoalData,
            targetDate,
            projectedHomePriceInfo,
            targetAmount,

            // update homePrice if projected amount changed using formula above
            ...(isProjectedHomePriceChanged && {
                homePrice: projectedHomePriceInfo?.amount,
            }),
        };
    },
    getRecommendedRiskScore: (config) =>
        getRecommendedRiskScoreUsingTargetDate(config),
    getTargetDate: ({ goalData }) => goalData.targetDate,
    ...getGoalBaseConfig('HOME_DOWN_PAYMENT'),
};

/**
 * 20 year YoY Home Price increase = 4.59%
 *
 * House Price Index YoY in the United States averaged 4.59 percent from 1992
 * until 2022, reaching an all time high of 19.30 percent in July of 2021 and a
 * record low of -10.60 percent in November of 2008. This page includes a chart
 * with historical data for the United States FHFA House Price Index YoY.
 * United States House Price Index YoY - data, historical chart, forecasts and
 * calendar of releases - was last updated on January of 2023.
 */
const HOME_PRICE_INCREASE_RATE = 0.0459;

const getProjectedHomePriceInfo = async ({
    targetDate,
    homeSize,
    metro,
}: {
    targetDate?: string | null;
    homeSize?: HomeSize;
    metro?: string;
}): Promise<HomePriceInfo | undefined> => {
    if (!targetDate || !homeSize || !metro) return undefined;

    // Fetch home price info via GraphQL
    const currentHomePriceInfo = (
        await client.query({
            query: HomePriceInfoDocument,
            variables: { homeSize, metro },
        })
    ).data.homePriceInfo;
    if (!currentHomePriceInfo) return undefined;

    const floatYearsUntilTargetGoal = Math.max(
        dt(targetDate).diff(dt(), 'years', true),
        0 // avoid negative numbers
    );

    const projectedAmountFloat = FV({
        startingBalance: currentHomePriceInfo.amount,
        interestRate: HOME_PRICE_INCREASE_RATE,
        numPeriods: floatYearsUntilTargetGoal,
    });

    // Round to nearest $1,000
    const projectedAmount =
        Math.round(projectedAmountFloat / 1_000_00) * 1_000_00;

    return {
        locationName: currentHomePriceInfo.locationName,
        amount: projectedAmount,
    };
};

/**
 * National average incl. escrow, credit check, loan application,
 * property appraisal, home inspection, title search fee, attorney
 */
const CLOSING_COSTS_FIXED_AMOUNT = 3400_00; // $3,400

/**
 * National average
 */
const CLOSING_COSTS_VARIABLE_RATE = 0.035; // 3.5%

const getDownPaymentAmount = ({
    homePrice,
    downPaymentFraction,
    includeClosingCostsAndFees,
}: {
    homePrice?: number;
    downPaymentFraction?: number;
    includeClosingCostsAndFees: boolean;
}): number | undefined => {
    if (!isNumber(homePrice) || !isNumber(downPaymentFraction))
        return undefined;

    let floatResult = homePrice * downPaymentFraction;

    if (includeClosingCostsAndFees) {
        floatResult +=
            CLOSING_COSTS_FIXED_AMOUNT +
            homePrice * CLOSING_COSTS_VARIABLE_RATE;
    }

    // Round to nearest $1,000
    const result = Math.round(floatResult / 1_000_00) * 1_000_00;

    return result;
};
